import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useTour } from '@/contexts/TourContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { 
  AlertCircle,
  Plus, 
  Trash2, 
  Edit3,
  MapPin,
  Eye,
  Loader2,
  Hash,
  Calendar,
  Clock,
  User,
  MessageSquare,
  Image,
  Search,
  Filter,
  Building,
  Package,
  Gauge,
  Send,
  X,
  CheckCircle2,
  AlertTriangle,
  Circle,
  Upload,
  ImagePlus,
  ZoomIn
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status options
const STATUS_OPTIONS = [
  { value: 'open', label: 'Open', color: 'bg-red-500', icon: Circle },
  { value: 'in_progress', label: 'In Progress', color: 'bg-yellow-500', icon: AlertTriangle },
  { value: 'closed', label: 'Closed', color: 'bg-green-500', icon: CheckCircle2 },
];

// Priority options
const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low', color: 'text-gray-500' },
  { value: 'medium', label: 'Medium', color: 'text-yellow-500' },
  { value: 'high', label: 'High', color: 'text-orange-500' },
  { value: 'critical', label: 'Critical', color: 'text-red-500' },
];

// Linked item types
const LINKED_ITEM_TYPES = [
  { value: 'none', label: 'None', icon: AlertCircle },
  { value: 'building', label: 'Building', icon: Building },
  { value: 'asset', label: 'Asset', icon: Package },
  { value: 'instrumentation', label: 'Instrumentation', icon: Gauge },
];

export function TicketsPanel({ 
  isOpen,
  onClose,
  currentSweep,
  cameraPosition,
  onNavigateToTicket
}) {
  const { token, user } = useAuth();
  const { currentTour } = useTour();
  const [tickets, setTickets] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [assets, setAssets] = useState([]);
  const [instruments, setInstruments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    tag_number: '',
    description: '',
    due_date: '',
    priority: 'medium',
    status: 'open',
    linked_item_type: 'none',
    linked_item_id: '',
    capturePosition: true,
  });

  // Fetch tickets
  const fetchTickets = useCallback(async () => {
    if (!currentTour?.id || !token) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/tickets?tour_id=${currentTour.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTickets(data);
      }
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  }, [currentTour?.id, token]);

  // Fetch linked items
  const fetchLinkedItems = useCallback(async () => {
    if (!currentTour?.id || !token) return;
    
    try {
      const [buildingsRes, assetsRes, instrumentsRes] = await Promise.all([
        fetch(`${API_URL}/api/buildings?tour_id=${currentTour.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/equipment/tour/${currentTour.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/instrumentation?tour_id=${currentTour.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (buildingsRes.ok) setBuildings(await buildingsRes.json());
      if (assetsRes.ok) setAssets(await assetsRes.json());
      if (instrumentsRes.ok) setInstruments(await instrumentsRes.json());
    } catch (error) {
      console.error('Error fetching linked items:', error);
    }
  }, [currentTour?.id, token]);

  useEffect(() => {
    if (isOpen) {
      fetchTickets();
      fetchLinkedItems();
    }
  }, [isOpen, fetchTickets, fetchLinkedItems]);

  // Filter tickets
  const filteredTickets = tickets.filter(ticket => {
    const matchesSearch = ticket.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         ticket.tag_number?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || ticket.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Get linked items based on type
  const getLinkedItems = () => {
    switch (formData.linked_item_type) {
      case 'building': return buildings;
      case 'asset': return assets;
      case 'instrumentation': return instruments;
      default: return [];
    }
  };

  // Get linked item name
  const getLinkedItemName = (type, id) => {
    let items = [];
    switch (type) {
      case 'building': items = buildings; break;
      case 'asset': items = assets; break;
      case 'instrumentation': items = instruments; break;
      default: return null;
    }
    const item = items.find(i => i.id === id);
    return item?.name || item?.tag_number || 'Unknown';
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token || !currentTour?.id) return;

    setSubmitting(true);
    try {
      const submitData = {
        ...formData,
        tour_id: currentTour.id,
        linked_item_name: formData.linked_item_id ? 
          getLinkedItemName(formData.linked_item_type, formData.linked_item_id) : null,
      };

      // Add position if enabled
      if (formData.capturePosition && currentSweep?.id) {
        submitData.sweep_id = currentSweep.id;
        if (cameraPosition?.position) submitData.position = cameraPosition.position;
        if (cameraPosition?.rotation) submitData.rotation = cameraPosition.rotation;
      }

      delete submitData.capturePosition;

      const response = await fetch(`${API_URL}/api/tickets`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submitData)
      });

      if (response.ok) {
        await fetchTickets();
        handleCloseDialog();
      }
    } catch (error) {
      console.error('Error creating ticket:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle status update
  const handleStatusUpdate = async (ticketId, newStatus) => {
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/tickets/${ticketId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        const updatedTicket = await response.json();
        setTickets(prev => prev.map(t => t.id === ticketId ? updatedTicket : t));
        if (selectedTicket?.id === ticketId) {
          setSelectedTicket(updatedTicket);
        }
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  // Handle add comment
  const handleAddComment = async () => {
    if (!token || !selectedTicket || !newComment.trim()) return;

    try {
      const response = await fetch(`${API_URL}/api/tickets/${selectedTicket.id}/activity`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'comment',
          details: newComment.trim()
        })
      });

      if (response.ok) {
        const updatedTicket = await response.json();
        setSelectedTicket(updatedTicket);
        setTickets(prev => prev.map(t => t.id === selectedTicket.id ? updatedTicket : t));
        setNewComment('');
      }
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  // Handle photo upload
  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !token || !selectedTicket) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Invalid file type. Please upload JPEG, PNG, GIF, or WebP images.');
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('File too large. Maximum size is 10MB.');
      return;
    }

    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_URL}/api/tickets/${selectedTicket.id}/photos/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const updatedTicket = await response.json();
        setSelectedTicket(updatedTicket);
        setTickets(prev => prev.map(t => t.id === selectedTicket.id ? updatedTicket : t));
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to upload photo');
      }
    } catch (error) {
      console.error('Error uploading photo:', error);
      alert('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Handle photo delete
  const handleDeletePhoto = async (photoId) => {
    if (!token || !selectedTicket || !window.confirm('Delete this photo?')) return;

    try {
      const response = await fetch(`${API_URL}/api/tickets/${selectedTicket.id}/photos/${photoId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        // Update local state
        const updatedPhotos = selectedTicket.photos.filter(p => p.id !== photoId);
        const updatedTicket = { ...selectedTicket, photos: updatedPhotos };
        setSelectedTicket(updatedTicket);
        setTickets(prev => prev.map(t => t.id === selectedTicket.id ? updatedTicket : t));
        setSelectedPhoto(null);
      }
    } catch (error) {
      console.error('Error deleting photo:', error);
    }
  };

  // Handle delete
  const handleDelete = async (ticketId) => {
    if (!token || !window.confirm('Are you sure you want to delete this ticket?')) return;

    try {
      const response = await fetch(`${API_URL}/api/tickets/${ticketId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setTickets(prev => prev.filter(t => t.id !== ticketId));
        if (selectedTicket?.id === ticketId) {
          setSelectedTicket(null);
          setIsDetailOpen(false);
        }
      }
    } catch (error) {
      console.error('Error deleting ticket:', error);
    }
  };

  // Handle dialog close
  const handleCloseDialog = () => {
    setIsCreateDialogOpen(false);
    setFormData({
      name: '',
      tag_number: '',
      description: '',
      due_date: '',
      priority: 'medium',
      status: 'open',
      linked_item_type: 'none',
      linked_item_id: '',
      capturePosition: true,
    });
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const option = STATUS_OPTIONS.find(s => s.value === status);
    if (!option) return null;
    return (
      <Badge className={`${option.color} text-white`}>
        {option.label}
      </Badge>
    );
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString();
  };

  // Format timestamp
  const formatTimestamp = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString();
  };

  return (
    <>
      <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <SheetContent side="right" className="w-[400px] sm:w-[540px] p-0">
          <SheetHeader className="p-6 pb-4 border-b">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-primary" />
              <SheetTitle>Active Issues</SheetTitle>
            </div>
            <SheetDescription>
              Track and manage facility issues and tickets
            </SheetDescription>
          </SheetHeader>

          <div className="p-4 border-b space-y-3">
            {/* Search and Filter */}
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search tickets..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[130px]">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {STATUS_OPTIONS.map(status => (
                    <SelectItem key={status.value} value={status.value}>
                      {status.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Stats */}
            <div className="flex gap-2">
              {STATUS_OPTIONS.map(status => {
                const count = tickets.filter(t => t.status === status.value).length;
                return (
                  <Button
                    key={status.value}
                    variant={statusFilter === status.value ? "default" : "outline"}
                    size="sm"
                    className="flex-1"
                    onClick={() => setStatusFilter(statusFilter === status.value ? 'all' : status.value)}
                  >
                    <span className={`w-2 h-2 rounded-full ${status.color} mr-2`} />
                    {count}
                  </Button>
                );
              })}
            </div>

            {/* Add Button */}
            <Button 
              onClick={() => setIsCreateDialogOpen(true)} 
              className="w-full gap-2"
            >
              <Plus className="h-4 w-4" />
              Create Ticket
            </Button>
          </div>

          {/* Tickets List */}
          <ScrollArea className="h-[calc(100vh-340px)]">
            <div className="p-4 space-y-3">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : filteredTickets.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <AlertCircle className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No tickets found</p>
                </div>
              ) : (
                filteredTickets.map(ticket => (
                  <div 
                    key={ticket.id}
                    className="p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors cursor-pointer"
                    onClick={() => {
                      setSelectedTicket(ticket);
                      setIsDetailOpen(true);
                    }}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium truncate">{ticket.name}</h4>
                          {getStatusBadge(ticket.status)}
                        </div>
                        {ticket.tag_number && (
                          <p className="text-sm text-muted-foreground flex items-center gap-1">
                            <Hash className="h-3 w-3" />
                            {ticket.tag_number}
                          </p>
                        )}
                        {ticket.linked_item_name && (
                          <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                            {ticket.linked_item_type === 'building' && <Building className="h-3 w-3" />}
                            {ticket.linked_item_type === 'asset' && <Package className="h-3 w-3" />}
                            {ticket.linked_item_type === 'instrumentation' && <Gauge className="h-3 w-3" />}
                            {ticket.linked_item_name}
                          </p>
                        )}
                        <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                          {ticket.due_date && (
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {formatDate(ticket.due_date)}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <MessageSquare className="h-3 w-3" />
                            {ticket.activity?.length || 0}
                          </span>
                          {ticket.photos?.length > 0 && (
                            <span className="flex items-center gap-1">
                              <Image className="h-3 w-3" />
                              {ticket.photos.length}
                            </span>
                          )}
                        </div>
                        {ticket.sweep_id && (
                          <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            Position saved
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        {ticket.sweep_id && onNavigateToTicket && (
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              onNavigateToTicket(ticket.sweep_id, {
                                rotation: ticket.rotation,
                                transitionTime: 1500
                              });
                            }}
                            title="Go to location"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>

      {/* Create Ticket Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={handleCloseDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Ticket</DialogTitle>
            <DialogDescription>
              Create a new issue ticket for this facility
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Ticket Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g., Valve Leak"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tag_number">Tag Number</Label>
                <Input
                  id="tag_number"
                  value={formData.tag_number}
                  onChange={(e) => setFormData({...formData, tag_number: e.target.value})}
                  placeholder="e.g., TKT-001"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select 
                  value={formData.priority} 
                  onValueChange={(value) => setFormData({...formData, priority: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITY_OPTIONS.map(p => (
                      <SelectItem key={p.value} value={p.value}>
                        <span className={p.color}>{p.label}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="due_date">Due Date</Label>
                <Input
                  id="due_date"
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Describe the issue..."
                rows={3}
              />
            </div>

            {/* Link to Item */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Link To</Label>
                <Select 
                  value={formData.linked_item_type} 
                  onValueChange={(value) => setFormData({...formData, linked_item_type: value, linked_item_id: ''})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LINKED_ITEM_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>
                        <div className="flex items-center gap-2">
                          <t.icon className="h-4 w-4" />
                          {t.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {formData.linked_item_type !== 'none' && (
                <div className="space-y-2">
                  <Label>Select {formData.linked_item_type}</Label>
                  <Select 
                    value={formData.linked_item_id} 
                    onValueChange={(value) => setFormData({...formData, linked_item_id: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={`Select ${formData.linked_item_type}`} />
                    </SelectTrigger>
                    <SelectContent>
                      {getLinkedItems().map(item => (
                        <SelectItem key={item.id} value={item.id}>
                          {item.name || item.tag_number}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Link to 3D Position */}
            <div className="p-3 rounded-lg border space-y-2">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-primary" />
                  Link to 3D Position
                </Label>
                <Button
                  type="button"
                  variant={formData.capturePosition ? "default" : "outline"}
                  size="sm"
                  onClick={() => setFormData(prev => ({ ...prev, capturePosition: !prev.capturePosition }))}
                >
                  {formData.capturePosition ? 'Enabled' : 'Disabled'}
                </Button>
              </div>
              {formData.capturePosition && currentSweep?.id && (
                <p className="text-xs text-green-600">✓ Current position will be saved</p>
              )}
              {formData.capturePosition && !currentSweep?.id && (
                <p className="text-xs text-yellow-600">⚠️ Navigate in the 3D viewer to capture position</p>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Create Ticket
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Ticket Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedTicket && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <DialogTitle className="flex items-center gap-2">
                    {selectedTicket.name}
                    {getStatusBadge(selectedTicket.status)}
                  </DialogTitle>
                </div>
                {selectedTicket.tag_number && (
                  <p className="text-sm text-muted-foreground">#{selectedTicket.tag_number}</p>
                )}
              </DialogHeader>

              <Tabs defaultValue="details" className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="details">Details</TabsTrigger>
                  <TabsTrigger value="activity">
                    Activity ({selectedTicket.activity?.length || 0})
                  </TabsTrigger>
                  <TabsTrigger value="photos">
                    Photos ({selectedTicket.photos?.length || 0})
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="details" className="space-y-4 mt-4">
                  {/* Status Update */}
                  <div className="flex items-center gap-2">
                    <Label>Status:</Label>
                    <Select 
                      value={selectedTicket.status} 
                      onValueChange={(value) => handleStatusUpdate(selectedTicket.id, value)}
                    >
                      <SelectTrigger className="w-[150px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map(s => (
                          <SelectItem key={s.value} value={s.value}>
                            {s.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {selectedTicket.description && (
                    <div>
                      <Label className="text-muted-foreground">Description</Label>
                      <p className="mt-1">{selectedTicket.description}</p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    {selectedTicket.priority && (
                      <div>
                        <Label className="text-muted-foreground">Priority</Label>
                        <p className={`capitalize ${PRIORITY_OPTIONS.find(p => p.value === selectedTicket.priority)?.color}`}>
                          {selectedTicket.priority}
                        </p>
                      </div>
                    )}
                    {selectedTicket.due_date && (
                      <div>
                        <Label className="text-muted-foreground">Due Date</Label>
                        <p>{formatDate(selectedTicket.due_date)}</p>
                      </div>
                    )}
                    {selectedTicket.linked_item_name && (
                      <div>
                        <Label className="text-muted-foreground">Linked To</Label>
                        <p className="flex items-center gap-1">
                          {selectedTicket.linked_item_type === 'building' && <Building className="h-4 w-4" />}
                          {selectedTicket.linked_item_type === 'asset' && <Package className="h-4 w-4" />}
                          {selectedTicket.linked_item_type === 'instrumentation' && <Gauge className="h-4 w-4" />}
                          {selectedTicket.linked_item_name}
                        </p>
                      </div>
                    )}
                    <div>
                      <Label className="text-muted-foreground">Created By</Label>
                      <p>{selectedTicket.created_by_name}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Created At</Label>
                      <p>{formatTimestamp(selectedTicket.created_at)}</p>
                    </div>
                  </div>

                  {selectedTicket.sweep_id && (
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={() => {
                        onNavigateToTicket?.(selectedTicket.sweep_id, {
                          rotation: selectedTicket.rotation,
                          transitionTime: 1500
                        });
                        setIsDetailOpen(false);
                        onClose();
                      }}
                    >
                      <MapPin className="h-4 w-4 mr-2" />
                      Go to Location
                    </Button>
                  )}
                </TabsContent>

                <TabsContent value="activity" className="mt-4">
                  <div className="space-y-4">
                    {/* Add Comment */}
                    <div className="flex gap-2">
                      <Input
                        placeholder="Add a comment..."
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
                      />
                      <Button onClick={handleAddComment} disabled={!newComment.trim()}>
                        <Send className="h-4 w-4" />
                      </Button>
                    </div>

                    {/* Activity Timeline */}
                    <ScrollArea className="h-[300px]">
                      <div className="space-y-3">
                        {selectedTicket.activity?.slice().reverse().map(activity => (
                          <div key={activity.id} className="p-3 rounded-lg bg-muted/50">
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium text-sm">{activity.user_name}</span>
                              <span className="text-xs text-muted-foreground">
                                {formatTimestamp(activity.timestamp)}
                              </span>
                            </div>
                            <p className="text-sm">
                              {activity.action === 'comment' ? (
                                activity.details
                              ) : activity.action === 'status_changed' ? (
                                <span>
                                  Changed status from <Badge variant="outline">{activity.old_value}</Badge> to{' '}
                                  <Badge variant="outline">{activity.new_value}</Badge>
                                </span>
                              ) : (
                                activity.details || activity.action
                              )}
                            </p>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </div>
                </TabsContent>

                <TabsContent value="photos" className="mt-4">
                  <div className="space-y-4">
                    {/* Upload Button */}
                    <div className="flex gap-2">
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handlePhotoUpload}
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        className="hidden"
                      />
                      <Button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingPhoto}
                        className="w-full"
                      >
                        {uploadingPhoto ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <ImagePlus className="h-4 w-4 mr-2" />
                            Add Photo
                          </>
                        )}
                      </Button>
                    </div>

                    {/* Photos Grid */}
                    {selectedTicket?.photos?.length > 0 ? (
                      <div className="grid grid-cols-3 gap-2">
                        {selectedTicket.photos.map(photo => (
                          <div 
                            key={photo.id} 
                            className="relative group aspect-square rounded-lg overflow-hidden bg-muted cursor-pointer"
                            onClick={() => setSelectedPhoto(photo)}
                          >
                            <img
                              src={photo.url}
                              alt={photo.filename}
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-white hover:bg-white/20"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedPhoto(photo);
                                }}
                              >
                                <ZoomIn className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-red-400 hover:bg-red-500/20"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeletePhoto(photo.id);
                                }}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <Image className="h-12 w-12 mx-auto mb-3 opacity-50" />
                        <p>No photos yet</p>
                        <p className="text-sm">Click &quot;Add Photo&quot; to upload images</p>
                      </div>
                    )}
                  </div>
                </TabsContent>
              </Tabs>

              <DialogFooter>
                <Button variant="destructive" size="sm" onClick={() => handleDelete(selectedTicket.id)}>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Ticket
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Photo Preview Dialog */}
      <Dialog open={!!selectedPhoto} onOpenChange={() => setSelectedPhoto(null)}>
        <DialogContent className="max-w-3xl p-0">
          {selectedPhoto && (
            <div className="relative">
              <img
                src={selectedPhoto.url}
                alt={selectedPhoto.filename}
                className="w-full max-h-[80vh] object-contain"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                <p className="text-white font-medium">{selectedPhoto.filename}</p>
                <p className="text-white/70 text-sm">
                  Uploaded by {selectedPhoto.uploaded_by_name} • {formatTimestamp(selectedPhoto.uploaded_at)}
                </p>
                {selectedPhoto.caption && (
                  <p className="text-white/90 mt-2">{selectedPhoto.caption}</p>
                )}
              </div>
              <Button
                variant="destructive"
                size="sm"
                className="absolute top-4 right-4"
                onClick={() => {
                  handleDeletePhoto(selectedPhoto.id);
                }}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default TicketsPanel;
